import { cn } from '@aibi/ui';
import { GripVertical } from 'lucide-react';
import type { CSSProperties } from 'react';
import type { CardDragSession } from './card-drag-session';

/**
 * Fantasma que sigue al puntero con el tamano real de la tarjeta (no el default).
 * El original queda como hueco en la rejilla.
 */
export function CardDragOverlay({ session }: { readonly session: CardDragSession }) {
  const compact = session.heightPx < 120;
  const style = {
    width: session.widthPx,
    height: session.heightPx,
  } satisfies CSSProperties;

  return (
    <div
      style={style}
      className={cn(
        'pointer-events-none flex flex-col overflow-hidden rounded-xl border border-primary/35 bg-card text-card-foreground',
        'shadow-overlay ring-2 ring-primary/25',
        'origin-center animate-[card-lift_180ms_cubic-bezier(0.2,0.8,0.2,1)_forwards]',
      )}
    >
      <header
        className={cn(
          'flex shrink-0 items-center gap-1 border-b border-border/70 bg-card/95 pl-1 pr-2 backdrop-blur-sm',
          compact ? 'h-8' : 'h-10',
        )}
      >
        <span className="flex size-7 items-center justify-center text-primary">
          <GripVertical aria-hidden className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{session.title}</span>
      </header>
      <div className="relative min-h-0 flex-1 bg-gradient-to-b from-card to-muted/30">
        {!compact && (
          <div
            aria-hidden
            className="absolute inset-2 rounded-md border border-dashed border-border/60 bg-muted/25"
          />
        )}
      </div>
    </div>
  );
}

/** Hueco en la rejilla que indica donde encajara la tarjeta al soltar (mismo w×h). */
export function CardSnapSlot({
  snap,
}: {
  readonly snap: NonNullable<CardDragSession['snap']>;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none rounded-xl border-2 border-dashed border-primary/55 bg-primary/10',
        'animate-[card-slot-pulse_1.1s_ease-in-out_infinite]',
      )}
      style={{
        gridColumn: `${String(snap.x + 1)} / span ${String(snap.w)}`,
        gridRow: `${String(snap.y + 1)} / span ${String(snap.h)}`,
      }}
    />
  );
}
